import { useEffect, useMemo, useRef, useState } from "react";

import { getProductionHistory, getProductionHistories } from "@/features/devices/api";
import { useHistoryCache } from "@/lib/HistoryCache";
import type {
  ProductionHistory,
  ProductionHistoryWindow,
} from "@/features/devices/types";
import { parseTag } from "@/features/machines/addressing";
import type { Machine } from "@/features/machines/types";
import type { DashboardWidget } from "./types";
import { widgetSources } from "./types";
import { widgetCapabilities } from "./capabilities";

export type WidgetHistoryState =
  | { status: "loading" }
  /**
   * Revalidacao em andamento: `data` e a serie anterior ou o cache da janela
   * pedida. O eixo continua seguindo a janela do DADO, com indicador de carga.
   */
  | { status: "reloading"; data: ProductionHistory }
  | { status: "ready"; data: ProductionHistory }
  | { status: "unavailable" }
  | { status: "error" };

interface HistoryTarget {
  widgetId: string;
  source: string;
}

interface HistoryRequest {
  key: string;
  identity: string;
  configHashHex?: string | null;
  deviceId: string;
  portIndex: number;
  window: ProductionHistoryWindow;
  targets: HistoryTarget[];
}

/**
 * Chave do mapa de estado. Precisa do par widget+fonte: um painel multi-fonte
 * tem N séries vivas ao mesmo tempo, e indexar só pelo widget faria a última
 * sobrescrever as anteriores.
 */
export function historyKeyFor(widgetId: string, source: string): string {
  return `${widgetId}::${source}`;
}

export function useProductionHistories(
  machine: Machine,
  widgets: DashboardWidget[],
  configHashesByDevice?: ReadonlyMap<string, string | null>,
  enabled = true,
): Map<string, WidgetHistoryState> {
  const requests = useMemo(
    () => buildHistoryRequests(machine, widgets, configHashesByDevice),
    [machine, widgets, configHashesByDevice],
  );
  const requestSignature = useMemo(
    () =>
      requests
        .map(
          (request) =>
            `${request.key}:${request.targets
              .map((target) => historyKeyFor(target.widgetId, target.source))
              .join(",")}`,
        )
        .join("|"),
    [requests],
  );
  const [states, setStates] = useState<Map<string, WidgetHistoryState>>(
    () => new Map(),
  );
  // Cache por sessao/workspace/device/porta/config/janela — voltar a uma rota
  // mostra a serie em revalidacao (nunca leitura atual). Ultima serie
  // boa por widget alimenta o estado `reloading` (dado antigo visível durante
  // a troca de janela, nunca card em branco).
  const cache = useHistoryCache();
  const lastByWidgetRef = useRef<Map<string, { identity: string; data: ProductionHistory }>>(new Map());
  // Cada retomada exige confirmacao nova. O token pertence ao render (nao a
  // uma ref mutada durante render), portanto uma transition abandonada nao
  // invalida a consulta que ainda esta visivel.
  const activity = useMemo(() => ({ enabled, requestSignature, cache }), [enabled, requestSignature, cache]);
  const [stateActivity, setStateActivity] = useState<object | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (requests.length === 0) {
      setStates(new Map());
      return;
    }

    let active = true;
    let controller = new AbortController();
    let pending = false;
    let queued = false;
    const forget = (request: HistoryRequest) => {
      cache.delete(request.key);
      for (const target of request.targets) lastByWidgetRef.current.delete(historyKeyFor(target.widgetId, target.source));
    };

    const load = async (showLoading: boolean) => {
      if (!active) return;
      if (pending) { queued = true; return; }
      pending = true;
      const generation = cache.generation;
      controller = new AbortController();
      if (showLoading) {
        setStateActivity(activity);
        setStates((current) => {
          const next = new Map(current);
          for (const request of requests) {
            const cached = cache.get(request.key);
            if (cached) {
              next.set(request.key, { status: "reloading", data: cached });
              continue;
            }
            const previous = request.targets.map((target) =>
              lastByWidgetRef.current.get(historyKeyFor(target.widgetId, target.source)),
            ).find((entry) => entry?.identity === request.identity);
            next.set(request.key, previous
              ? { status: "reloading", data: previous.data }
              : { status: "loading" });
          }
          return next;
        });
      }

      const groups = new Map<string, HistoryRequest[]>();
      for (const request of requests) {
        const groupKey = JSON.stringify([request.deviceId, request.configHashHex, request.window]);
        const group = groups.get(groupKey) ?? [];
        group.push(request);
        groups.set(groupKey, group);
      }
      await Promise.all(
        [...groups.values()].map(async (group) => {
          const first = group[0];
          try {
            const rows = group.length === 1
              ? [await getProductionHistory(first.deviceId, first.portIndex, first.window, controller.signal)]
              : await getProductionHistories(first.deviceId, group.map((r) => r.portIndex), first.window, controller.signal);
            if (!active || generation !== cache.generation) return;
            const updates = new Map<string, WidgetHistoryState>();
            for (const request of group) {
              const data = rows.find((row) => row.portIndex === request.portIndex);
              if (!data || data.deviceId !== request.deviceId || data.window !== request.window) {
                forget(request);
                updates.set(request.key, { status: "error" });
                continue;
              }
              // O meta pode mudar entre as respostas do painel e do historico.
              // Nao pintar uma serie de outra configuracao sob o nome atual.
              if (data.configHashHex && data.configHashHex !== request.configHashHex) {
                forget(request);
                updates.set(request.key, { status: "loading" });
                continue;
              }
              cache.set(request.key, data);
              const stateKeys = request.targets.map((target) =>
                historyKeyFor(target.widgetId, target.source),
              );
              for (const stateKey of stateKeys) {
                lastByWidgetRef.current.set(stateKey, { identity: request.identity, data });
              }
              updates.set(request.key, { status: "ready", data });
            }
            setStates((current) => new Map([...current, ...updates]));
          } catch (error) {
            if (!active || generation !== cache.generation || (error as { name?: string }).name === "AbortError") return;
            const status = (error as { status?: number }).status;
            const nextState: WidgetHistoryState =
              status === 503 ? { status: "unavailable" } : { status: "error" };
            for (const request of group) forget(request);
            setStates((current) =>
              assignState(
                current,
                group.map((request) => request.key),
                nextState,
              ),
            );
          }
        }),
      );
      pending = false;
      if (active && queued) {
        queued = false;
        void load(false);
      }
    };

    void load(true);
    const interval = window.setInterval(() => void load(false), 60_000);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(interval);
    };
    // `requestSignature` serializa `requests` e é a chave de dedupe deliberada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestSignature, cache, enabled, activity]);

  // A projecao usa a identidade ATUAL ja no render da troca. Esperar o effect
  // limpar o mapa deixaria um frame com tipo do rele rotulado como corrente.
  return useMemo(() => {
    const byTarget = new Map<string, WidgetHistoryState>();
    for (const request of requests) {
      for (const target of request.targets) {
        const state = states.get(request.key);
        // Nao ha nem um frame ready do ciclo anterior no retorno da pausa.
        const projected = enabled && stateActivity === activity ? state :
          state && "data" in state ? { status: "reloading" as const, data: state.data } : undefined;
        byTarget.set(historyKeyFor(target.widgetId, target.source),
          projected ?? { status: "loading" });
      }
    }
    return byTarget;
  }, [requests, states, enabled, stateActivity, activity]);
}

function buildHistoryRequests(
  machine: Machine,
  widgets: DashboardWidget[],
  configHashesByDevice?: ReadonlyMap<string, string | null>,
): HistoryRequest[] {
  const byKey = new Map<string, HistoryRequest>();

  for (const widget of widgets) {
    const window = historyWindowForWidget(widget);
    if (!window) continue;

    // Uma requisição POR FONTE. O dedupe por `device:porta:janela` já existia
    // e continua valendo: duas séries do mesmo ponto (ou dois widgets sobre a
    // mesma porta) compartilham uma única chamada.
    for (const source of widgetSources(widget)) {
      const address = parseTag(source);
      if (!address) continue;
      const deviceId = machine.slots.find(
        (slot) => slot.slotIndex === address.slotIndex,
      )?.deviceId;
      if (!deviceId) continue;

      const configHashHex = configHashesByDevice?.get(deviceId);
      const identity = `${machine.workspaceId}:${deviceId}:${address.portIndex}:${configHashHex ?? "unknown"}`;
      const key = `${identity}:${window}`;
      const existing = byKey.get(key);
      // `widgetIds` virou `targets`: um widget multi-fonte aparece uma vez por
      // fonte, e o estado passa a ser indexado por `widgetId::source`. Antes,
      // com a chave só do widget, a segunda série sobrescrevia a primeira.
      const target = { widgetId: widget.id, source };
      if (existing) {
        existing.targets.push(target);
      } else {
        byKey.set(key, {
          key,
          identity,
          configHashHex,
          deviceId,
          portIndex: address.portIndex,
          window,
          targets: [target],
        });
      }
    }
  }

  return [...byKey.values()];
}

export function historyWindowForWidget(
  widget: DashboardWidget,
): ProductionHistoryWindow | null {
  // A tabela decide QUEM participa; a condicao de settings continua aqui.
  if (!widgetCapabilities(widget.widgetType).history) return null;

  if (widget.widgetType !== "production-kpi") {
    return widget.settings.chart?.window ?? widget.settings.history?.window ?? "24h";
  }
  if (widget.settings.production?.mode !== "total") return null;
  const window = widget.settings.production.window;
  return window && window !== "current" ? window : null;
}

function assignState(
  current: Map<string, WidgetHistoryState>,
  stateKeys: string[],
  state: WidgetHistoryState,
): Map<string, WidgetHistoryState> {
  const next = new Map(current);
  for (const stateKey of stateKeys) next.set(stateKey, state);
  return next;
}
